import { useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  Activity,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Loader2,
  GripHorizontal,
  LogIn,
  FilePlus,
  FileX,
  CheckCircle,
  XCircle,
  UserCog,
  UserCheck,
  UserX,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http, API_URL } from '@/network/http';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { useLocale } from '@/hooks/useLocale';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  useAnimatedNumber,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { GlassCard } from '@/components/glass';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

// ============================================================================
// Types
// ============================================================================

interface AuditLogEntry {
  id: string;
  userId: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  details: Record<string, unknown> | null;
  ipAddress: string | null;
  userName: string | null;
  userEmail: string | null;
  createdAt: string;
}

interface AuditListResponse {
  success: boolean;
  data: AuditLogEntry[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

interface AuditStatsResponse {
  success: boolean;
  data: {
    total: number;
    byAction: { action: string; count: number }[];
  };
}

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 30;

const ACTION_COLORS: Record<string, string> = {
  LOGIN: 'bg-blue-100 text-blue-700',
  REPORT_CREATE: 'bg-green-100 text-green-700',
  REPORT_DELETE: 'bg-red-100 text-red-700',
  REPORT_APPROVE: 'bg-emerald-100 text-emerald-700',
  REPORT_REJECT: 'bg-orange-100 text-orange-700',
  ROLE_CHANGE: 'bg-purple-100 text-purple-700',
  USER_ACTIVATE: 'bg-teal-100 text-teal-700',
  USER_DEACTIVATE: 'bg-neutral-200 text-neutral-700',
  USER_APPROVE: 'bg-green-100 text-green-700',
  USER_REJECT: 'bg-red-100 text-red-700',
};

const ACTIONS = [
  'LOGIN',
  'REPORT_CREATE',
  'REPORT_DELETE',
  'REPORT_APPROVE',
  'REPORT_REJECT',
  'ROLE_CHANGE',
  'USER_ACTIVATE',
  'USER_DEACTIVATE',
  'USER_APPROVE',
  'USER_REJECT',
];

const ACTION_ICONS: Record<string, typeof Activity> = {
  LOGIN: LogIn,
  REPORT_CREATE: FilePlus,
  REPORT_DELETE: FileX,
  REPORT_APPROVE: CheckCircle,
  REPORT_REJECT: XCircle,
  ROLE_CHANGE: UserCog,
  USER_ACTIVATE: UserCheck,
  USER_DEACTIVATE: UserX,
  USER_APPROVE: UserCheck,
  USER_REJECT: UserX,
};


// ============================================================================
// Helpers
// ============================================================================

function formatDate(dateString: string, locale: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function getActionIcon(action: string) {
  const Icon = ACTION_ICONS[action] || Activity;
  return Icon;
}

// ============================================================================
// AnimatedValue Component
// ============================================================================

function AnimatedValue({ value }: { value: number }) {
  const d = useAnimatedNumber(value);
  return <>{d.toLocaleString()}</>;
}

// ============================================================================
// Main Component
// ============================================================================

export function ActivityLogPage() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const locale = useLocale();
  const { hasPermission } = usePermissions();

  // ============================================================================
  // State
  // ============================================================================

  const [params, setParams] = useQueryParams({
    action: { default: '' },
    from: { default: '' },
    to: { default: '' },
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);

  // ============================================================================
  // Queries
  // ============================================================================

  // Stats query
  const { data: statsData } = useQuery<AuditStatsResponse>({
    queryKey: ['audit-stats', params.from, params.to],
    queryFn: async () => {
      const queryParams: Record<string, string> = {};
      if (params.from) queryParams.startDate = params.from;
      if (params.to) queryParams.endDate = params.to;
      const response = await http.get('/audit/stats', { params: queryParams });
      return response.data;
    },
  });

  // Infinite scroll query
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery<AuditListResponse>({
    queryKey: ['audit-logs', 'infinite', params.action, params.from, params.to],
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams: Record<string, string | number> = {
        page: pageParam as number,
        limit: PAGE_SIZE,
      };
      if (params.action) queryParams.action = params.action;
      if (params.from) queryParams.startDate = params.from;
      if (params.to) queryParams.endDate = params.to;
      const response = await http.get('/audit', { params: queryParams });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.pagination) return undefined;
      const { page, pages } = lastPage.pagination;
      return page < pages ? page + 1 : undefined;
    },
  });

  // ============================================================================
  // Infinite scroll
  // ============================================================================

  const sentinelRef = useInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleExport = useCallback(() => {
    const exportParams = new URLSearchParams();
    if (params.action) exportParams.set('action', params.action);
    if (params.from) exportParams.set('startDate', params.from);
    if (params.to) exportParams.set('endDate', params.to);
    window.open(`${API_URL}/audit/export?${exportParams.toString()}`, '_blank');
    triggerHapticFeedback();
  }, [params.action, params.from, params.to]);

  const handleClearFilters = useCallback(() => {
    setParams({ action: '', from: '', to: '' });
    triggerHapticFeedback();
  }, [setParams]);

  const handleToggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev);
    triggerHapticFeedback();
  }, []);

  const handleLogClick = useCallback((log: AuditLogEntry) => {
    setSelectedLog(log);
    triggerHapticFeedback();
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedLog(null);
    triggerHapticFeedback();
  }, []);

  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: { offset: { y: number } }) => {
      if (info.offset.y > 150) {
        handleCloseModal();
      }
    },
    [handleCloseModal]
  );

  // ============================================================================
  // Computed values
  // ============================================================================

  const stats = useMemo(() => {
    if (!statsData?.data) {
      return { total: 0, topActions: [] };
    }
    const topActions = [...statsData.data.byAction]
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
    return {
      total: statsData.data.total,
      topActions,
    };
  }, [statsData]);

  const allLogs = useMemo(() => {
    if (!data?.pages) return [];
    const seen = new Set<string>();
    return data.pages.flatMap((page) => page.data).filter((log) => {
      if (seen.has(log.id)) return false;
      seen.add(log.id);
      return true;
    });
  }, [data]);

  const hasActiveFilters = useMemo(() => {
    return !!(params.action || params.from || params.to);
  }, [params.action, params.from, params.to]);

  const totalCount = data?.pages[0]?.pagination?.total ?? 0;

  // ============================================================================
  // Render: Header
  // ============================================================================

  const renderHeader = () => (
    <motion.div
      className="mb-6"
      variants={staggerItem}
    >
      <div className="flex items-start gap-4 mb-2">
        <div className="w-14 h-14 rounded-lg flex items-center justify-center border border-gray-200 glass-surface">
          <Activity className="w-7 h-7 text-slate-800" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">
            {t('audit.activityLog')}
          </h1>
          <p className="text-neutral-600 mt-1">
            {t('audit.viewSystemActivity')}
          </p>
        </div>
      </div>
    </motion.div>
  );

  // ============================================================================
  // Render: Stats
  // ============================================================================

  const renderStats = () => (
    <motion.div
      className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
      variants={staggerContainer}
    >
      {/* Total Events */}
      <motion.div
        variants={staggerItem}
        className="p-4 rounded-xl border border-gray-200 glass-surface"
      >
        <div className="flex items-center gap-2 mb-2">
          <Activity className="w-4 h-4 shrink-0" style={{ color: ADMIN_COLORS.navy + '80' }} />
          <div className="text-xs font-bold text-slate-500 uppercase">
            {t('audit.totalEvents')}
          </div>
        </div>
        <div className="text-2xl font-bold text-slate-800">
          <AnimatedValue value={stats.total} />
        </div>
      </motion.div>

      {/* Top 3 actions */}
      {stats.topActions.map((actionStat) => {
        const ActionStatIcon = ACTION_ICONS[actionStat.action] || Activity;
        return (
          <motion.div
            key={actionStat.action}
            variants={staggerItem}
            className="p-4 rounded-xl border border-gray-200 glass-surface"
          >
            <div className="flex items-center gap-2 mb-2">
              <ActionStatIcon className="w-4 h-4 shrink-0" style={{ color: ADMIN_COLORS.navy + '80' }} />
              <div className="text-xs font-bold text-slate-500 uppercase">
                {t(`audit.actions.${actionStat.action}`)}
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-800">
              <AnimatedValue value={actionStat.count} />
            </div>
          </motion.div>
        );
      })}

      {/* Fill remaining slots if less than 3 top actions */}
      {Array.from({ length: Math.max(0, 3 - stats.topActions.length) }).map((_, i) => (
        <motion.div
          key={`empty-${i}`}
          variants={staggerItem}
          className="p-4 rounded-xl border border-gray-200 glass-surface"
        >
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 shrink-0" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <div className="text-xs font-bold text-slate-500 uppercase">
              {t('audit.noData')}
            </div>
          </div>
          <div className="text-2xl font-bold text-slate-800">-</div>
        </motion.div>
      ))}
    </motion.div>
  );

  // ============================================================================
  // Render: Filter/Export Toolbar
  // ============================================================================

  const renderToolbar = () => (
    <motion.div
      variants={staggerItem}
      className="flex items-center gap-3 mb-4"
    >
      <button
        onClick={handleToggleFilters}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 font-medium transition-all hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none active:translate-x-1 active:translate-y-1 active:shadow-none"
        style={{
          backgroundColor: hasActiveFilters ? ADMIN_COLORS.ice : 'white',
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <Filter className="w-4 h-4" />
        {t('audit.filters')}
        {showFilters ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>

      {hasPermission('action:export_data') && (
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 font-medium bg-white transition-all hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none active:translate-x-1 active:translate-y-1 active:shadow-none"
          style={{
            boxShadow: ADMIN_COLORS.shadow,
          }}
        >
          <Download className="w-4 h-4" />
          {t('audit.exportCsv')}
        </button>
      )}

      <div className="flex-1" />

      <div className="text-sm text-neutral-600">
        {t('audit.totalResults', { count: totalCount })}
      </div>
    </motion.div>
  );

  // ============================================================================
  // Render: Collapsible Filters
  // ============================================================================

  const renderFilters = () => (
    <AnimatePresence>
      {showFilters && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ type: 'spring' as const, stiffness: 300, damping: 30 }}
          className="overflow-hidden mb-4"
        >
          <GlassCard title={t('audit.filters')}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Action filter */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('audit.filterByAction')}
                </label>
                <select
                  value={params.action}
                  onChange={(e) => setParams({ action: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('audit.allActions')}</option>
                  {ACTIONS.map((action) => (
                    <option key={action} value={action}>
                      {t(`audit.actions.${action}`)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Start date */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('audit.startDate')}
                </label>
                <input
                  type="date"
                  value={params.from}
                  onChange={(e) => setParams({ from: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* End date */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('audit.endDate')}
                </label>
                <input
                  type="date"
                  value={params.to}
                  onChange={(e) => setParams({ to: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Clear button */}
              <div className="flex items-end">
                <button
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 font-medium bg-white transition-all hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none active:translate-x-1 active:translate-y-1 active:shadow-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0"
                  style={{
                    boxShadow: hasActiveFilters ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  {t('audit.clearFilters')}
                </button>
              </div>
            </div>
          </GlassCard>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // ============================================================================
  // Render: Log Entry Card
  // ============================================================================

  const renderLogCard = (log: AuditLogEntry, _index: number) => {
    const Icon = getActionIcon(log.action);
    const actionColor = ACTION_COLORS[log.action] || 'bg-neutral-100 text-neutral-700';

    return (
      <motion.div
        key={log.id}
        variants={staggerItem}
        onClick={() => handleLogClick(log)}
        className="p-4 rounded-xl border border-gray-200 glass-surface cursor-pointer transition-all hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none active:translate-x-1 active:translate-y-1 active:shadow-none"
        style={{
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <div className="flex items-start gap-3">
          {/* Icon circle */}
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${actionColor}`}
          >
            <Icon className="w-5 h-5" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${actionColor}`}>
                {t(`audit.actions.${log.action}`)}
              </span>
              {log.entityType && (
                <span className="text-xs text-neutral-500">
                  {t(`audit.entityTypes.${log.entityType}`, { defaultValue: log.entityType })}
                </span>
              )}
            </div>

            <div className="text-sm font-medium text-neutral-800 mb-1">
              {log.userName || t('audit.unknownUser')}
            </div>

            {log.userEmail && (
              <div className="text-xs text-neutral-600 mb-1">
                {log.userEmail}
              </div>
            )}

            <div className="text-xs text-neutral-500">
              {formatDate(log.createdAt, locale)}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // ============================================================================
  // Render: Log List
  // ============================================================================

  const renderLogList = () => {
    if (isLoading) {
      return (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex flex-col items-center justify-center py-16"
        >
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
          <p className="text-neutral-600">{t('audit.loading')}</p>
        </motion.div>
      );
    }

    if (isError) {
      return (
        <motion.div
          key="error"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="text-center py-16"
        >
          <p className="text-red-600 font-medium mb-2">{t('audit.loadError')}</p>
          <p className="text-neutral-600 text-sm">{t('audit.tryAgain')}</p>
        </motion.div>
      );
    }

    if (allLogs.length === 0) {
      return (
        <motion.div
          key="empty"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="text-center py-16"
        >
          <Activity className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
          <p className="text-neutral-600 font-medium mb-1">{t('audit.noLogs')}</p>
          <p className="text-neutral-500 text-sm">{t('audit.tryDifferentFilters')}</p>
        </motion.div>
      );
    }

    return (
      <motion.div
        key="content"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        exit={{ opacity: 0 }}
        className="space-y-3"
      >
        {allLogs.map((log, index) => renderLogCard(log, index))}

        {/* Sentinel for infinite scroll */}
        <div ref={sentinelRef} className="h-4" />

        {/* Loading more indicator */}
        {isFetchingNextPage && (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        )}

        {/* End of list message */}
        {!hasNextPage && allLogs.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-6 text-sm text-neutral-500"
          >
            {t('audit.endOfList')}
          </motion.div>
        )}
      </motion.div>
    );
  };

  // ============================================================================
  // Render: Desktop Detail Modal
  // ============================================================================

  const renderDesktopModal = () => {
    if (!selectedLog) return null;

    const Icon = getActionIcon(selectedLog.action);
    const actionColor = ACTION_COLORS[selectedLog.action] || 'bg-neutral-100 text-neutral-700';

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ left: '8rem' }}
        onClick={handleCloseModal}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" />

        {/* Modal content */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring' as const, stiffness: 300, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-2xl max-h-[90dvh] overflow-y-auto rounded-3xl border border-gray-200 glass-surface"
          style={{ boxShadow: ADMIN_COLORS.shadow }}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b-2 border-gray-200 p-6 flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${actionColor}`}>
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${actionColor}`}>
                  {t(`audit.actions.${selectedLog.action}`)}
                </span>
                <h2 className="text-xl font-bold text-neutral-900 mt-2">
                  {t('audit.logDetails')}
                </h2>
              </div>
            </div>
            <button
              onClick={handleCloseModal}
              className="p-2 rounded-lg border border-gray-200 bg-white transition-all hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none"
              style={{ boxShadow: ADMIN_COLORS.shadow }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* User info */}
            <div>
              <div className="text-sm font-medium text-neutral-500 mb-1">
                {t('audit.user')}
              </div>
              <div className="text-base font-medium text-neutral-800">
                {selectedLog.userName || t('audit.unknownUser')}
              </div>
              {selectedLog.userEmail && (
                <div className="text-sm text-neutral-500">
                  {selectedLog.userEmail}
                </div>
              )}
            </div>

            {/* Entity info */}
            {selectedLog.entityType && (
              <div>
                <div className="text-sm font-medium text-neutral-500 mb-1">
                  {t('audit.entity')}
                </div>
                <div className="text-base font-medium text-neutral-800">
                  {t(`audit.entityTypes.${selectedLog.entityType}`, { defaultValue: selectedLog.entityType })}
                  {selectedLog.entityId && (
                    <span className="text-neutral-500 ml-2">
                      (ID: {selectedLog.entityId})
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Timestamp */}
            <div>
              <div className="text-sm font-medium text-neutral-500 mb-1">
                {t('audit.timestamp')}
              </div>
              <div className="text-base font-medium text-neutral-800">
                {formatDate(selectedLog.createdAt, locale)}
              </div>
            </div>

            {/* IP Address */}
            {selectedLog.ipAddress && (
              <div>
                <div className="text-sm font-medium text-neutral-500 mb-1">
                  {t('audit.ipAddress')}
                </div>
                <div className="text-base font-medium text-neutral-800 font-mono">
                  {selectedLog.ipAddress}
                </div>
              </div>
            )}

            {/* Details */}
            {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
              <div>
                <div className="text-sm font-medium text-neutral-500 mb-2">
                  {t('audit.details')}
                </div>
                <div className="p-4 rounded-lg border border-gray-200 bg-neutral-50">
                  <dl className="space-y-2">
                    {Object.entries(selectedLog.details).map(([key, value]) => (
                      <div key={key}>
                        <dt className="text-sm font-medium text-neutral-800">
                          {key}:
                        </dt>
                        <dd className="text-sm font-medium text-neutral-800 mt-0.5 font-mono">
                          {typeof value === 'object'
                            ? JSON.stringify(value, null, 2)
                            : String(value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // ============================================================================
  // Render: Mobile Detail Modal
  // ============================================================================

  const renderMobileModal = () => {
    if (!selectedLog) return null;

    const Icon = getActionIcon(selectedLog.action);
    const actionColor = ACTION_COLORS[selectedLog.action] || 'bg-neutral-100 text-neutral-700';

    const modalContent = (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end"
        onClick={handleCloseModal}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" />

        {/* Drawer */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.7 }}
          onDragEnd={handleDragEnd}
          transition={{ type: 'spring' as const, stiffness: 300, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-h-[90dvh] overflow-y-auto rounded-t-2xl border border-gray-200 bg-white pb-safe"
          style={{ boxShadow: '0 -2px 8px rgba(0,0,0,0.1)' }}
        >
          {/* Drag handle */}
          <div className="sticky top-0 bg-white border-b-2 border-gray-200 p-4 flex flex-col items-center">
            <GripHorizontal className="w-8 h-8 text-neutral-400 mb-2" />
            <div className="w-full flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${actionColor}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${actionColor}`}>
                    {t(`audit.actions.${selectedLog.action}`)}
                  </span>
                  <h2 className="text-xl font-bold text-neutral-900 mt-2">
                    {t('audit.logDetails')}
                  </h2>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 rounded-lg border border-gray-200 bg-white"
                style={{ boxShadow: ADMIN_COLORS.shadow }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* User info */}
            <div>
              <div className="text-sm font-medium text-neutral-500 mb-1">
                {t('audit.user')}
              </div>
              <div className="text-base font-medium text-neutral-800">
                {selectedLog.userName || t('audit.unknownUser')}
              </div>
              {selectedLog.userEmail && (
                <div className="text-sm text-neutral-500">
                  {selectedLog.userEmail}
                </div>
              )}
            </div>

            {/* Entity info */}
            {selectedLog.entityType && (
              <div>
                <div className="text-sm font-medium text-neutral-500 mb-1">
                  {t('audit.entity')}
                </div>
                <div className="text-base font-medium text-neutral-800">
                  {t(`audit.entityTypes.${selectedLog.entityType}`, { defaultValue: selectedLog.entityType })}
                  {selectedLog.entityId && (
                    <span className="text-neutral-500 ml-2">
                      (ID: {selectedLog.entityId})
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Timestamp */}
            <div>
              <div className="text-sm font-medium text-neutral-500 mb-1">
                {t('audit.timestamp')}
              </div>
              <div className="text-base font-medium text-neutral-800">
                {formatDate(selectedLog.createdAt, locale)}
              </div>
            </div>

            {/* IP Address */}
            {selectedLog.ipAddress && (
              <div>
                <div className="text-sm font-medium text-neutral-500 mb-1">
                  {t('audit.ipAddress')}
                </div>
                <div className="text-base font-medium text-neutral-800 font-mono">
                  {selectedLog.ipAddress}
                </div>
              </div>
            )}

            {/* Details */}
            {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
              <div>
                <div className="text-sm font-medium text-neutral-500 mb-2">
                  {t('audit.details')}
                </div>
                <div className="p-4 rounded-lg border border-gray-200 bg-neutral-50">
                  <dl className="space-y-2">
                    {Object.entries(selectedLog.details).map(([key, value]) => (
                      <div key={key}>
                        <dt className="text-sm font-medium text-neutral-800">
                          {key}:
                        </dt>
                        <dd className="text-sm font-medium text-neutral-800 mt-0.5 font-mono break-all">
                          {typeof value === 'object'
                            ? JSON.stringify(value, null, 2)
                            : String(value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    );

    return createPortal(modalContent, document.body);
  };

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        {renderHeader()}
        {renderStats()}
        {renderToolbar()}
        {renderFilters()}

        <AnimatePresence mode="wait">
          {renderLogList()}
        </AnimatePresence>
      </motion.div>

      {/* Modals */}
      <AnimatePresence>
        {selectedLog && (
          isMobile ? renderMobileModal() : renderDesktopModal()
        )}
      </AnimatePresence>
    </div>
  );
}
